'use client';

import { useCallback, useMemo, useState } from 'react';
import { lowerCase } from 'lodash';
import { Button, Card, CardBody, DatePicker, Icon, Select } from '@repo/ui';

import MatterLedgerReport from './MatterLedgerReport';
import { LedgerType, LedgerReportForm as FormType, LedgerReportFormError } from '../../types';
import { LEDGER_TYPE_OPTIONS, LedgerTypeMapLabel } from '../../constants';
import { useGetCategories } from '../../queries';

export default function LedgerReportForm() {
  const [isGenerateReport, setIsGenerateReport] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formValues, setFormValues] = useState<FormType>({
    runDate: null,
    dateRange: { from: null, to: null },
    ledgerType: null,
    ledgerData: null,
  });

  const [errors, setErrors] = useState<LedgerReportFormError>({});

  const { data: categories, isLoading: isLoadingLedgerOptions } = useGetCategories(
    formValues.ledgerType
  );

  const isValidForm = () => {
    const newErrors: LedgerReportFormError = {};

    if (!formValues.ledgerType) {
      newErrors.ledgerType = 'Please select ledger report type';
    } else if (!formValues.ledgerData) {
      newErrors.ledgerData = `Please select ${lowerCase(LedgerTypeMapLabel[formValues.ledgerType])} for ledger report`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      return false;
    }

    return true;
  };

  const handleGenerate = () => {
    if (!isValidForm()) return;

    setLoading(true);

    setTimeout(() => {
      setIsGenerateReport(true);
      setLoading(false);
    }, 600);
  };

  const update = useCallback(
    (patch: Partial<FormType>) => {
      setFormValues({ ...formValues, ...patch });

      // Remove errors for updated fields
      setErrors(prevErrors => {
        const updatedErrors = { ...prevErrors };
        Object.keys(patch).forEach(key => {
          if (key in updatedErrors) {
            delete updatedErrors[key as keyof LedgerReportFormError];
          }
        });
        return updatedErrors;
      });
    },
    [formValues]
  );

  const ledgerDataOptions = useMemo(() => categories, [categories]);

  return (
    <>
      {isGenerateReport ? (
        <>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Icon name="HiArrowLeft" />}
            onClick={() => setIsGenerateReport(false)}
          >
            Back To Form
          </Button>
          <MatterLedgerReport formValues={formValues} />
        </>
      ) : (
        <Card>
          <div className="flex flex-col gap-2 mb-6 text-xtnd-dark dark:text-xtnd-light">
            <h3 className="text-lg font-semibold">Generate Ledger Report</h3>
            <span className="text-sm text-gray-500">
              Select date range (optional) and how to group the ledger data
            </span>
          </div>

          <CardBody>
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-4">
                <DatePicker
                  mode="single"
                  label="Date From (Optional)"
                  placeholder="Pick a date"
                  allowClear
                  value={formValues.dateRange.from}
                  onChange={from => update({ dateRange: { ...formValues.dateRange, from } })}
                />

                <DatePicker
                  mode="single"
                  label="Date To (Optional)"
                  placeholder="Pick a date"
                  allowClear
                  value={formValues.dateRange.to}
                  onChange={to => update({ dateRange: { ...formValues.dateRange, to } })}
                />
              </div>

              <Select
                label="Ledger Type"
                required
                placeholder="Select ledger type"
                options={LEDGER_TYPE_OPTIONS}
                value={formValues.ledgerType}
                onChange={option => {
                  if (typeof option === 'string') {
                    update({ ledgerType: option as LedgerType, ledgerData: null });
                    return;
                  }

                  update({ ledgerData: null, ledgerType: option?.value as any as LedgerType });
                }}
                error={errors.ledgerType}
                fullWidth
              />

              {formValues.ledgerType && (
                <Select
                  required
                  isLoading={isLoadingLedgerOptions}
                  disabled={isLoadingLedgerOptions}
                  label={`${LedgerTypeMapLabel[formValues.ledgerType]}`}
                  placeholder={`Select ${LedgerTypeMapLabel[formValues.ledgerType]}`}
                  options={ledgerDataOptions || []}
                  value={formValues.ledgerData}
                  onChange={option => {
                    if (typeof option === 'string') {
                      update({ ledgerData: option });
                      return;
                    }

                    update({ ledgerData: option?.value });
                  }}
                  error={errors.ledgerData}
                  searchable
                  fullWidth
                />
              )}

              <Button
                className="w-full"
                disabled={loading || !formValues.ledgerType}
                onClick={handleGenerate}
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
}
